import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertTriangle, Calendar, BookOpen, Stethoscope, Bell } from "lucide-react";
import { useAlerts, Alert } from "@/hooks/useAlerts";

const getIcon = (type: Alert["type"]) => {
  switch (type) {
    case "medical":
      return <Stethoscope className="h-4 w-4" />;
    case "exam":
      return <BookOpen className="h-4 w-4" />;
    case "appointment":
      return <Calendar className="h-4 w-4" />;
    case "subject":
      return <BookOpen className="h-4 w-4" />;
    default:
      return <Bell className="h-4 w-4" />;
  }
};

const getPriorityStyles = (priority: Alert["priority"]) => {
  switch (priority) {
    case "urgent":
      return "bg-destructive/10 border-destructive/30 hover:bg-destructive/20";
    case "warning":
      return "bg-yellow-500/10 border-yellow-500/30 hover:bg-yellow-500/20";
    case "info":
      return "bg-blue-500/10 border-blue-500/30 hover:bg-blue-500/20";
    default:
      return "bg-muted hover:bg-muted/80";
  }
};

const getPriorityBadge = (priority: Alert["priority"]) => {
  switch (priority) {
    case "urgent":
      return <Badge variant="destructive" className="text-xs">Urgent</Badge>;
    case "warning":
      return <Badge className="bg-yellow-500 text-white text-xs">Warning</Badge>;
    case "info":
      return <Badge variant="secondary" className="text-xs">Info</Badge>;
    default:
      return null;
  }
};

interface AlertItemProps {
  alert: Alert;
  onClick: () => void;
}

const AlertItem = ({ alert, onClick }: AlertItemProps) => (
  <div
    className={`p-3 rounded-lg border cursor-pointer transition-colors ${getPriorityStyles(alert.priority)}`}
    onClick={onClick}
  >
    <div className="flex items-start gap-3">
      <div className="mt-0.5 text-muted-foreground">
        {getIcon(alert.type)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-medium text-sm truncate">{alert.title}</span>
          {getPriorityBadge(alert.priority)}
        </div>
        <p className="text-xs text-muted-foreground">{alert.message}</p>
        {alert.daysRemaining !== undefined && (
          <p className="text-xs font-medium mt-1 text-primary">
            {alert.daysRemaining} days remaining
          </p>
        )}
      </div>
    </div>
  </div>
);

export const AlertsWidget = () => {
  const navigate = useNavigate();
  const { data: alerts = [], isLoading } = useAlerts();

  const urgentAlerts = alerts.filter(a => a.priority === "urgent");
  const warningAlerts = alerts.filter(a => a.priority === "warning");
  const infoAlerts = alerts.filter(a => a.priority === "info");

  const medicalAlerts = alerts.filter(a => a.type === "medical" || a.type === "appointment");
  const examAlerts = alerts.filter(a => a.type === "exam");
  const subjectAlerts = alerts.filter(a => a.type === "subject");

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Alerts & Reminders
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Loading alerts...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Alerts & Reminders
          </CardTitle>
          <div className="flex gap-1">
            {urgentAlerts.length > 0 && (
              <Badge variant="destructive">{urgentAlerts.length} urgent</Badge>
            )}
            {warningAlerts.length > 0 && (
              <Badge className="bg-yellow-500">{warningAlerts.length} warning</Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-2">
        {alerts.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No active alerts</p>
          </div>
        ) : (
          <Tabs defaultValue="all" className="w-full">
            <TabsList className="grid w-full grid-cols-4 mb-3">
              <TabsTrigger value="all" className="text-xs">All ({alerts.length})</TabsTrigger>
              <TabsTrigger value="medical" className="text-xs">Medical ({medicalAlerts.length})</TabsTrigger>
              <TabsTrigger value="exam" className="text-xs">Exam ({examAlerts.length})</TabsTrigger>
              <TabsTrigger value="subject" className="text-xs">Subject ({subjectAlerts.length})</TabsTrigger>
            </TabsList>

            <ScrollArea className="h-[280px]">
              <TabsContent value="all" className="mt-0 space-y-2">
                {alerts.slice(0, 15).map((alert) => (
                  <AlertItem
                    key={alert.id}
                    alert={alert}
                    onClick={() => navigate(alert.link)}
                  />
                ))}
                {alerts.length > 15 && (
                  <p className="text-xs text-center text-muted-foreground py-2">
                    +{alerts.length - 15} more alerts
                  </p>
                )}
              </TabsContent>

              <TabsContent value="medical" className="mt-0 space-y-2">
                {medicalAlerts.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No medical alerts</p>
                ) : (
                  medicalAlerts.map((alert) => (
                    <AlertItem
                      key={alert.id}
                      alert={alert}
                      onClick={() => navigate(alert.link)}
                    />
                  ))
                )}
              </TabsContent>

              <TabsContent value="exam" className="mt-0 space-y-2">
                {examAlerts.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No exam alerts</p>
                ) : (
                  examAlerts.map((alert) => (
                    <AlertItem
                      key={alert.id}
                      alert={alert}
                      onClick={() => navigate(alert.link)}
                    />
                  ))
                )}
              </TabsContent>

              <TabsContent value="subject" className="mt-0 space-y-2">
                {subjectAlerts.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No subject alerts</p>
                ) : (
                  subjectAlerts.map((alert) => (
                    <AlertItem
                      key={alert.id}
                      alert={alert}
                      onClick={() => navigate(alert.link)}
                    />
                  ))
                )}
              </TabsContent>
            </ScrollArea>
          </Tabs>
        )}
      </CardContent>
    </Card>
  );
};
